import React from "react";
import {
  Button,
  Box,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { DeleteOutlined, CloseOutlined } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import BDrawer from "../BDrawer";
import { InfoCircle } from "iconsax-react";

export interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  itemName?: string;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  onConfirm,
  itemName,
  title,
  message,
  confirmText,
  cancelText,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const resolvedTitle = title ?? t("confirmations.deleteItemTitle");
  const resolvedConfirmText = confirmText ?? t("common.delete");
  const resolvedCancelText = cancelText ?? t("common.cancel");

  const defaultMessage = itemName
    ? t("confirmations.deleteItem", { name: itemName })
    : t("confirmations.deleteItemGeneric");

  const displayMessage = message || defaultMessage;

  const handleConfirm = () => {
    onConfirm();
    onClose();
  };

  // Action buttons component
  const actionButtons = (
    <>
      <Button
        onClick={onClose}
        startIcon={<CloseOutlined />}
        color="inherit"
        fullWidth
        variant="outlined"
      >
        {resolvedCancelText}
      </Button>
      <Button
        onClick={handleConfirm}
        startIcon={<DeleteOutlined />}
        color="error"
        fullWidth
        variant="outlined"
      >
        {resolvedConfirmText}
      </Button>
    </>
  );

  // Bottom drawer for smaller screens
  if (isMobile) {
    return (
      <BDrawer
        open={open}
        onClose={onClose}
        anchor="bottom"
        title={
          <Box display="flex" alignItems="center" gap={0.5}>
            <InfoCircle variant="Bulk" size={24} color={theme.palette.error.main} />
            <Box component="span">{resolvedTitle}</Box>
          </Box>
        }
        showTopBar={true}
        showBottomBar={false}
        dialogActions={
          <Box sx={{ display: "flex", gap: 1, width: "100%", px: 1 }}>
            {actionButtons}
          </Box>
        }
      >
        <Box sx={{ pt: 2, px: 3 }}>
          <Box component="span" sx={{ color: theme.palette.text.primary }}>
            {displayMessage}
          </Box>
        </Box>
      </BDrawer>
    );
  }

  // Dialog for larger screens
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={0.5}>
          <InfoCircle variant="Bulk" size={24} color={theme.palette.error.main} />
          <Box component="span" sx={{ fontWeight: 600 }}>
            {resolvedTitle}
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box component="span" sx={{ color: theme.palette.text.primary }}>
          {displayMessage}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        {actionButtons}
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;

